<svelte:options runes={false} />
<script>
  // Market footprint (TASK-20260823-twap-strategies-tca): what the market did around the parent —
  // drift with/against, participation, interval VWAP (labelled approx.), 5-minute reversion, slice
  // latency, pace adherence and the posture mix. Every metric is an honest "—" with the scorecard's
  // note when its input is missing.
  import { fmtBps, fmtPct } from '../../../lib/twapReport.js';
  import { formatQuoteAmount } from '../../../lib/quoteCurrency.js';

  export let scorecard = null;
  export let parent = null;

  const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
  const latency = (ms) => (isNum(ms) ? (ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`) : '—');

  $: sc = scorecard ?? {};
  $: ccy = parent?.quoteCurrency ?? 'USD';
  $: driftText = isNum(sc.marketDriftBps)
    ? `${Math.abs(sc.marketDriftBps).toFixed(1)} bps ${sc.marketDriftBps > 0 ? 'against' : sc.marketDriftBps < 0 ? 'in favour' : 'flat'}`
    : '—';
  $: metrics = [
    { label: 'Market drift (arrival → end)', value: driftText, tone: isNum(sc.marketDriftBps) ? (sc.marketDriftBps > 0 ? 'loss' : 'gain') : 'neutral',
      sub: isNum(sc.marketDriftQuote) ? `${formatQuoteAmount(Math.abs(sc.marketDriftQuote), ccy)} on the order size` : 'context only — not a saving' },
    { label: 'Participation', value: isNum(sc.participationPct) ? fmtPct(sc.participationPct, { signed: false, decimals: 1 }) : '—',
      sub: 'our qty / other 1-minute volume in the window' },
    { label: 'Interval VWAP', value: isNum(sc.intervalVwap) ? formatQuoteAmount(sc.intervalVwap, ccy) : '—', sub: 'approx. (1 m OHLCV typical price)',
      extra: isNum(sc.intervalVwap) && isNum(sc.avgFillPrice) ? `avg fill ${formatQuoteAmount(sc.avgFillPrice, ccy)}` : null },
    { label: 'Reversion 5 m after end', value: fmtBps(sc.reversionBps5m), tone: isNum(sc.reversionBps5m) ? (sc.reversionBps5m > 0 ? 'gain' : sc.reversionBps5m < 0 ? 'loss' : 'neutral') : 'neutral',
      sub: isNum(sc.reversionBps5m) ? 'positive = price moved away after we finished' : 'available five minutes after terminal' },
    { label: 'Slice latency p50 / p95', value: `${latency(sc.sliceLatencyP50Ms)} / ${latency(sc.sliceLatencyP95Ms)}`, sub: 'placed → resolved' },
    { label: 'Pace adherence', value: isNum(sc.paceAdherence) ? fmtPct(sc.paceAdherence * 100, { signed: false, decimals: 1 }) : '—', sub: '1 − mean |pace offset|' },
  ];
  $: mix = Array.isArray(sc.postureMix) ? sc.postureMix : [];
</script>

<div class="mf">
  <dl class="mf-grid">
    {#each metrics as m}
      <div class="mf-item">
        <dt>{m.label}</dt>
        <dd class={`mf-value tone-${m.tone ?? 'neutral'}`}>{m.value}</dd>
        {#if m.extra}<dd class="mf-sub">{m.extra}</dd>{/if}
        <dd class="mf-sub">{m.sub}</dd>
      </div>
    {/each}
  </dl>
  <div class="mf-mix">
    <span class="mf-label">Posture mix (share of filled qty)</span>
    {#if mix.length === 0}
      <span class="mf-sub">—</span>
    {:else}
      <div class="mf-bar" role="img" aria-label={mix.map((m) => `${m.postureName} ${isNum(m.pct) ? m.pct.toFixed(0) : '?'} %`).join(', ')}>
        {#each mix as m}
          <span class={`mf-seg mf-seg-${m.posture}`} style={`flex: ${isNum(m.pct) ? m.pct : 0} 0 0`} title={`${m.postureName}: ${isNum(m.pct) ? m.pct.toFixed(1) : '?'} %`}></span>
        {/each}
      </div>
      <ul class="mf-mix-list">
        {#each mix as m}<li><span class={`mf-dot mf-seg-${m.posture}`}></span>{m.postureName} <strong>{isNum(m.pct) ? `${m.pct.toFixed(0)} %` : '—'}</strong></li>{/each}
      </ul>
    {/if}
  </div>
</div>

<style>
  .mf { width: 100%; min-width: 0; }
  .mf-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: var(--spacing-sm, 0.5rem); margin: 0; }
  .mf-item { background: var(--bg-secondary, #111214); border: 1px solid var(--border-primary, #1e1f22); border-radius: var(--radius-base, 0.5rem); padding: 0.5rem 0.65rem; min-width: 0; }
  .mf-item dt, .mf-label { font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); text-transform: uppercase; letter-spacing: 0.04em; }
  .mf-item dd { margin: 0; }
  .mf-value { font-size: var(--font-size-lg, 1.1rem); font-weight: var(--font-weight-semibold, 600); font-variant-numeric: tabular-nums; color: var(--text-primary, #fff); }
  .tone-gain { color: var(--color-success, #00ff88); }
  .tone-loss { color: var(--color-danger, #ff3b3b); }
  .mf-sub { font-size: var(--font-size-xs, 0.7rem); color: var(--text-muted, #6a6b6d); }
  .mf-mix { margin-top: var(--spacing-sm, 0.5rem); display: flex; flex-direction: column; gap: 0.3rem; }
  .mf-bar { display: flex; height: 10px; border-radius: 999px; overflow: hidden; background: var(--bg-quaternary, #212226); }
  .mf-seg { display: block; min-width: 0; }
  .mf-seg-0 { background: var(--color-danger, #ff3b3b); }
  .mf-seg-1 { background: var(--color-warning, #ffaa00); }
  .mf-seg-2 { background: var(--color-primary, #00d4ff); }
  .mf-seg-3 { background: var(--color-primary-dark, #0099cc); }
  .mf-seg-4 { background: var(--color-success, #00ff88); }
  .mf-mix-list { display: flex; flex-wrap: wrap; gap: 0.75rem; list-style: none; padding: 0; margin: 0; font-size: var(--font-size-xs, 0.7rem); color: var(--text-secondary, #8a8b8d); }
  .mf-mix-list strong { color: var(--text-primary, #fff); font-variant-numeric: tabular-nums; }
  .mf-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 0.3rem; }
</style>
